/** A small, reproducible 32-bit PRNG (Mulberry32). */
export class SeededRandom {
  private state: number;

  constructor(seed: number) {
    if (!Number.isFinite(seed)) {
      throw new TypeError('seed must be a finite number');
    }
    this.state = seed >>> 0;
  }

  /** Returns a value in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let value = this.state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  }

  /** Returns an integer between min and max, inclusive. */
  integer(min: number, max: number): number {
    if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max) || max < min) {
      throw new RangeError('integer bounds must be safe integers with max >= min');
    }
    return min + Math.floor(this.next() * (max - min + 1));
  }

  int(min: number, max: number): number {
    return this.integer(min, max);
  }

  /** Returns a floating-point value in [min, max). */
  range(min: number, max: number): number {
    if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) {
      throw new RangeError('range bounds must be finite with max >= min');
    }
    return min + this.next() * (max - min);
  }

  boolean(probability = 0.5): boolean {
    if (probability < 0 || probability > 1 || !Number.isFinite(probability)) {
      throw new RangeError('probability must be between 0 and 1');
    }
    return this.next() < probability;
  }

  pick<T>(values: readonly T[]): T {
    if (values.length === 0) {
      throw new RangeError('cannot pick from an empty array');
    }
    return values[this.integer(0, values.length - 1)] as T;
  }

  shuffle<T>(values: readonly T[]): T[] {
    const result = [...values];
    for (let index = result.length - 1; index > 0; index -= 1) {
      const swapIndex = this.integer(0, index);
      [result[index], result[swapIndex]] = [result[swapIndex] as T, result[index] as T];
    }
    return result;
  }
}

export function createSeededRandom(seed: number): SeededRandom {
  return new SeededRandom(seed);
}

export function createRandomFunction(seed: number): () => number {
  const random = createSeededRandom(seed);
  return () => random.next();
}

/** Stable string-to-seed helper for protocol and session identifiers. */
export function hashSeed(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}
